import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaCalendarAlt, FaMapMarkerAlt, FaRobot, FaUserTie, FaProjectDiagram, FaFlask, FaInfoCircle, FaBrain, FaLightbulb, FaUsers } from 'react-icons/fa';
import { trlLevels } from '../components/TRLInfo';
import TRLModal from '../components/TRLModal';
import CategoryModal from '../components/CategoryModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function ShomalAIFestival() {
  const [formData, setFormData] = useState({
    festival_id: 'shomal_ai_2025',
    tech_core_name: '',
    first_name: '',
    last_name: '',
    mobile: '',
    status: 'دانشجو',
    education_level: 'کارشناسی',
    idea_title: '',
    idea_problem: '',
    idea_solution: '',
    project_nature: 'نوآوری',
    team_size: '1',
    category_main: '',
    category_main_name: '',
    category_tech: '',
    category_tech_name: '',
    trl_level: '1'
  });
  
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTRLModalOpen, setIsTRLModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategorySelect = (selection) => {
    setSelectedCategory(selection);
    setFormData(prev => ({
      ...prev,
      category_main: selection.main,
      category_main_name: selection.mainName,
      category_tech: selection.tech,
      category_tech_name: selection.techName
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      await axios.post(`${API_URL}/api/registrations`, formData);
      setSubmitStatus({ 
        type: 'success', 
        message: 'ثبت‌نام شما با موفقیت انجام شد! به زودی با شما تماس خواهیم گرفت.' 
      });
      
      setFormData({
        festival_id: 'shomal_ai_2025',
        tech_core_name: '',
        first_name: '',
        last_name: '',
        mobile: '',
        status: 'دانشجو',
        education_level: 'کارشناسی',
        idea_title: '',
        idea_problem: '',
        idea_solution: '',
        project_nature: 'نوآوری',
        team_size: '1',
        category_main: '',
        category_main_name: '',
        category_tech: '',
        category_tech_name: '',
        trl_level: '1'
      });
      setSelectedCategory(null);

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'خطا در ثبت‌نام. لطفاً دوباره تلاش کنید یا با ما تماس بگیرید.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300 py-20">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>SHOMAL AI Connect 2025 | همایش تخصصی هوش مصنوعی</title>
        <meta name="description" content="همایش تخصصی هوش مصنوعی در دانشگاه شمال - ارتباط، نوآوری و آینده‌سازی با AI" />
        <meta name="keywords" content="هوش مصنوعی، AI، دانشگاه شمال، همایش، آرش سروری، Shomal AI Connect" />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Festival Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-6 py-3 rounded-2xl font-bold text-lg shadow-2xl animate-pulse">
              <FaRobot className="text-2xl" />
              ثبت‌نام فعال است
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-primary-700 via-secondary-600 to-accent-600 bg-clip-text text-transparent">
            SHOMAL AI Connect 2025
          </h1>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            همایش تخصصی هوش مصنوعی در دانشگاه شمال
          </h2>

          <p className="text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            ارتباط، نوآوری و آینده‌سازی با هوش مصنوعی
          </p>
        </motion.div>

        {/* Festival Poster */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-2xl border-2 border-primary-200 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-500 transition-all"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_906637cf-e765-4c1b-8807-5e812e951cd7/artifacts/e6tvuvgm_2025-12-06%20%20%20Shomal%20Ai%20Connect%20Poster%20V05_page-0001.jpg"
              alt="پوستر SHOMAL AI Connect 2025"
              className="w-full h-auto object-contain"
            />
          </motion.div>
        </div>

        {/* Event Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-5xl mx-auto mb-12 md:mb-16"
        >
          <div className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-6 md:p-10 shadow-2xl border-2 border-primary-200 dark:border-primary-700">
            <h3 className="text-2xl md:text-3xl font-bold text-primary-800 dark:text-primary-400 mb-8 flex items-center gap-3">
              <FaBrain className="text-3xl" />
              اطلاعات رویداد
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white dark:bg-gray-700 rounded-2xl p-6 border border-primary-200 dark:border-primary-600"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center">
                    <FaCalendarAlt className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">تاریخ برگزاری</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-200">شنبه، ۶ دی ۱۴۰۴</p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white dark:bg-gray-700 rounded-2xl p-6 border border-secondary-200 dark:border-secondary-600"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-secondary-600 to-secondary-500 rounded-xl flex items-center justify-center">
                    <FaCalendarAlt className="text-gray-900 text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">ساعت برگزاری</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-200">۹:۳۰ تا ۱۸:۰۰</p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white dark:bg-gray-700 rounded-2xl p-6 border border-accent-200 dark:border-accent-600 md:col-span-2"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-accent-600 to-accent-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FaMapMarkerAlt className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">مکان برگزاری</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-200">سالن همایش‌های بین‌المللی آمارد</p>
                    <p className="text-md text-gray-600 dark:text-gray-400">دانشگاه شمال - آمل</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Event Sections */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="max-w-5xl mx-auto mb-12 md:mb-16"
        >
          <div className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-6 md:p-10 shadow-2xl border-2 border-secondary-200 dark:border-secondary-700">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary-800 dark:text-secondary-400 mb-8 flex items-center gap-3">
              <FaLightbulb className="text-3xl text-secondary-600 dark:text-secondary-400" />
              بخش‌های رویداد
            </h3>
            
            <div className="space-y-4">
              {[
                { icon: FaBrain, title: "سخنرانی تخصصی در باره آینده هوش مصنوعی", color: "from-primary-600 to-primary-700" },
                { icon: FaRobot, title: "معرفی کاربرد AI در صنعت و دانشگاه", color: "from-secondary-600 to-secondary-500" },
                { icon: FaUsers, title: "پنل گفت‌وگو با متخصصان هوش مصنوعی", color: "from-accent-600 to-accent-700" },
                { icon: FaLightbulb, title: "شبکه‌سازی و ارتباط با فعالان حوزه فناوری", color: "from-primary-500 to-primary-700" }
              ].map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  whileHover={{ scale: 1.02, x: 10 }}
                  className="bg-white dark:bg-gray-700 rounded-xl p-5 border border-gray-200 dark:border-gray-600 flex items-center gap-4"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${section.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <section.icon className="text-white text-xl" />
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg">{section.title}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Registration Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="max-w-5xl mx-auto"
          id="registration-form"
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 md:p-10 lg:p-12 shadow-2xl border-2 border-primary-200 dark:border-primary-700">
            <div className="text-center mb-8 md:mb-12">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-6 py-3 rounded-2xl font-bold text-lg shadow-xl mb-6">
                <FaCheckCircle className="text-2xl" />
                ثبت‌نام در همایش
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-primary-700 to-secondary-600 bg-clip-text text-transparent">
                فرم ثبت‌نام
              </h2>

              <p className="text-base md:text-lg text-gray-600 dark:text-gray-300">
                در این همایش شرکت کنید و از آخرین دستاوردها در حوزه هوش مصنوعی مطلع شوید
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="registration-form">
              {/* Personal Info */}
              <div className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-5 md:p-8 border-2 border-primary-200 dark:border-primary-700">
                <h3 className="text-xl md:text-2xl font-bold mb-6 text-primary-800 dark:text-primary-400 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-r from-primary-700 to-primary-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg">
                    <FaUserTie />
                  </span>
                  مشخصات هسته فناور
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">نام هسته فناور *</label>
                    <input
                      type="text"
                      name="tech_core_name"
                      value={formData.tech_core_name}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all text-sm md:text-base"
                      placeholder="نام هسته فناور خود را وارد کنید"
                      data-testid="tech-core-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">نام *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white placeholder-gray-400 transition-all text-sm md:text-base"
                      placeholder="نام"
                      data-testid="first-name"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">نام خانوادگی *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white placeholder-gray-400 transition-all text-sm md:text-base"
                      placeholder="نام خانوادگی"
                      data-testid="last-name"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">تلفن همراه *</label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      required
                      pattern="[0-9]{11}"
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white placeholder-gray-400 transition-all text-sm md:text-base"
                      placeholder="09123456789"
                      data-testid="mobile"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">وضعیت *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white transition-all text-sm md:text-base"
                      data-testid="status"
                    >
                      <option value="دانشجو">دانشجو</option>
                      <option value="فارغ‌التحصیل">فارغ‌التحصیل</option>
                      <option value="دانش‌آموز">دانش‌آموز</option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">مدرک تحصیلی *</label>
                    <select
                      name="education_level"
                      value={formData.education_level}
                      onChange={handleInputChange}
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white transition-all text-sm md:text-base"
                      data-testid="education-level"
                    >
                      <option value="کارشناسی">کارشناسی</option>
                      <option value="کارشناسی ارشد">کارشناسی ارشد</option>
                      <option value="دکتری">دکتری</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">عنوان کامل ایده محوری *</label>
                  <input
                    type="text"
                    name="idea_title"
                    value={formData.idea_title}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white placeholder-gray-400 transition-all text-sm md:text-base"
                    placeholder="عنوان ایده خود را وارد کنید"
                    data-testid="idea-title"
                  />
                </div>

                <div className="mt-6">
                  <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">شرح مسئله یا چالش</label>
                  <textarea
                    name="idea_problem"
                    value={formData.idea_problem}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white placeholder-gray-400 transition-all text-sm md:text-base"
                    placeholder="مسئله یا چالشی که قصد حل آن را دارید..."
                    data-testid="idea-problem"
                  />
                </div>

                <div className="mt-6">
                  <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">راه حل پیشنهادی</label>
                  <textarea
                    name="idea_solution"
                    value={formData.idea_solution}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white placeholder-gray-400 transition-all text-sm md:text-base"
                    placeholder="راه حل خود را شرح دهید..."
                    data-testid="idea-solution"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4 md:gap-6 mt-6">
                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">ماهیت طرح *</label>
                    <select
                      name="project_nature"
                      value={formData.project_nature}
                      onChange={handleInputChange}
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white transition-all text-sm md:text-base"
                      data-testid="project-nature"
                    >
                      <option value="نوآوری">نوآوری</option>
                      <option value="مهندسی معکوس">مهندسی معکوس</option>
                      <option value="بهینه‌سازی">بهینه‌سازی</option>
                      <option value="سایر">سایر</option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">تعداد افراد تیم</label>
                    <input
                      type="number"
                      name="team_size"
                      value={formData.team_size}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-white placeholder-gray-400 transition-all text-sm md:text-base"
                      data-testid="team-size"
                    />
                  </div>
                </div>
              </div>

              {/* Category */}
              <div className="bg-gradient-to-br from-secondary-50 to-yellow-50 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-5 md:p-8 border-2 border-secondary-200 dark:border-secondary-700">
                <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-6 text-gray-600 dark:text-gray-400 flex items-center gap-3">
                  <span className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg text-xl md:text-2xl">
                    <FaProjectDiagram />
                  </span>
                  <span className="leading-tight">حوزه کالا و خدمات دانش‌بنیان</span>
                </h3>
                
                <div className="space-y-4">
                  <div className="flex flex-col gap-4">
                    <label className="font-bold text-gray-700 dark:text-gray-300 text-base md:text-lg">
                      دسته اصلی و فناوری *
                    </label>
                    <motion.button
                      type="button"
                      onClick={() => setIsCategoryModalOpen(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full flex items-center justify-center gap-3 px-6 py-4 md:py-5 bg-gradient-to-r from-secondary-600 to-secondary-500 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold text-base md:text-lg shadow-xl hover:shadow-2xl transition-all"
                    >
                      <FaInfoCircle className="text-xl" />
                      {selectedCategory ? 'تغییر انتخاب حوزه' : 'انتخاب حوزه دانش‌بنیان'}
                    </motion.button>
                  </div>
                  
                  {selectedCategory ? (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white dark:bg-gray-700 rounded-xl p-5 md:p-6 border-2 border-secondary-200 dark:border-secondary-700 shadow-lg"
                    >
                      <div className="mb-4 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                        <span className="text-sm text-gray-600 dark:text-gray-400 block mb-2">دسته اصلی:</span>
                        <p className="text-base md:text-lg font-bold text-gray-800 dark:text-white leading-relaxed">{selectedCategory.mainName}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400 block mb-2">دسته فناوری:</span>
                        <p className="text-base md:text-lg font-bold text-gray-800 dark:text-gray-300 leading-relaxed">{selectedCategory.techName}</p>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="bg-slate-900/30 rounded-xl p-6 md:p-8 text-center border-2 border-dashed border-secondary-200 dark:border-secondary-700">
                      <FaInfoCircle className="text-4xl text-purple-400 mx-auto mb-3" />
                      <p className="text-gray-700 dark:text-gray-300 text-base md:text-lg font-semibold leading-relaxed">
                        لطفاً حوزه کالا و خدمات دانش‌بنیان خود را از دکمه بالا انتخاب کنید
                      </p>
                    </div>
                  )}
                  <input 
                    type="hidden" 
                    name="category_main" 
                    value={formData.category_main} 
                    required 
                  />
                </div>
              </div>

              {/* TRL Level */}
              <div className="bg-gradient-to-br from-accent-50 to-green-50 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-5 md:p-8 border-2 border-accent-200 dark:border-accent-700">
                <h3 className="text-xl md:text-2xl font-bold mb-6 text-gray-600 dark:text-gray-400 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-r from-accent-600 to-accent-700 rounded-xl flex items-center justify-center text-white font-bold shadow-lg">
                    <FaFlask />
                  </span>
                  سطح آمادگی فناوری (TRL)
                </h3>
                
                <div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-3">
                    <label className="font-bold text-gray-700 dark:text-gray-300 text-sm md:text-base">
                      سطح TRL فعلی پروژه شما *
                    </label>
                    <motion.button
                      type="button"
                      onClick={() => setIsTRLModalOpen(true)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-sm shadow-md transition-all"
                    >
                      <FaInfoCircle />
                      مشاهده توضیحات سطوح
                    </motion.button>
                  </div>
                  <select
                    name="trl_level"
                    value={formData.trl_level}
                    onChange={handleInputChange}
                    className="w-full p-3 md:p-4 bg-white dark:bg-gray-700 border-2 border-accent-200 dark:border-accent-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-600 text-gray-800 dark:text-white transition-all text-sm md:text-base"
                    data-testid="trl-level"
                  >
                    {trlLevels.map(trl => (
                      <option key={trl.level} value={trl.level}>
                        سطح {trl.level} - {trl.shortDesc}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-4 md:pt-6">
                <motion.button
                  type="submit"
                  disabled={isSubmitting || !formData.category_main}
                  whileHover={{ scale: isSubmitting || !formData.category_main ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting || !formData.category_main ? 1 : 0.98 }}
                  className={`w-full md:w-auto px-10 md:px-16 py-4 md:py-5 rounded-2xl font-bold text-lg md:text-xl text-white shadow-2xl transition-all ${
                    isSubmitting || !formData.category_main
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 hover:shadow-primary-500/50'
                  }`}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-3">
                      <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      در حال ارسال...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-3">
                      <FaCheckCircle className="text-xl" />
                      ثبت‌نام در همایش
                    </span>
                  )}
                </motion.button>
              </div>

              {/* Submit Status Message */}
              {submitStatus.message && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`text-center p-4 rounded-xl ${
                    submitStatus.type === 'success'
                      ? 'bg-green-100 dark:bg-green-900/30 text-gray-700 dark:text-gray-300 border-2 border-green-600'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-2 border-red-600'
                  }`}
                >
                  <p className="font-bold text-lg">{submitStatus.message}</p>
                </motion.div>
              )}
            </form>
          </div>
        </motion.div>
      </div>

      {/* TRL Modal */}
      <TRLModal
        isOpen={isTRLModalOpen}
        onClose={() => setIsTRLModalOpen(false)}
        selectedLevel={formData.trl_level}
        onSelectLevel={(level) => {
          setFormData(prev => ({ ...prev, trl_level: level }));
        }}
      />

      {/* Category Modal */}
      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSelect={handleCategorySelect}
        currentSelection={selectedCategory}
      />
    </div>
  );
}

export default ShomalAIFestival;
